/**
 * Environment policy defaults — plan §5.2, §5.3, §6.1, §6.4, §8.3, §16.3 D,
 * §17.4, §17.5.
 *
 * Every dial the Admin policy pages expose, with its default, its plan
 * reference and a plain-language description. The Admin UI renders itself from
 * this table, so adding a setting means adding one entry here rather than a
 * migration plus a form field.
 */

export interface PolicyDial<T = number | string | boolean> {
  key: string;
  default: T;
  section: PolicySection;
  ref: string;
  label: string;
  /** Shown under the field. States what changing it actually affects. */
  effect: string;
  /**
   * A closed set of values. Rendered as a select rather than a text field —
   * a dial with three valid values should not be a box you can typo into.
   * Each choice states its own consequence, because "derived_only" versus
   * "unrestricted" is a data-governance decision, not a preference.
   */
  choices?: readonly { value: string; label: string; effect: string }[];
  /**
   * Populated at render time from a live source rather than from this file.
   * `ai_models` asks the configured provider what it currently serves, so the
   * list cannot go stale the way a hardcoded one does.
   */
  dynamicChoices?: 'ai_models';
  /** Rendered as a textarea. For dials whose value is a paragraph, not a number. */
  multiline?: boolean;
}

export type PolicySection =
  | 'scheduler'
  | 'jobchange'
  | 'caps'
  | 'throttle'
  | 'compliance'
  | 'guards'
  | 'tracking'
  | 'ranking'
  | 'ai'
  | 'prospecting'
  | 'verification'
  | 'jobs'
  | 'booking'
  | 'api';

export const POLICY_DIALS = [
  // ---- Scheduler (§5.1, §5.2) ------------------------------------------
  {
    key: 'scheduler.environment_daily_budget',
    default: 0,
    section: 'scheduler',
    ref: '§5.3',
    label: 'Environment daily send budget',
    effect: 'Hard ceiling across every mailbox. 0 means unset — mailbox and domain caps govern.',
  },
  {
    key: 'scheduler.promotion_lead_time_hours',
    default: 24,
    section: 'scheduler',
    ref: '§5.1',
    label: 'Urgent-band promotion lead time',
    effect: 'A task whose deadline falls inside this window jumps to the URGENT band, ahead of class.',
  },
  {
    key: 'scheduler.starvation_alert_days',
    default: 3,
    section: 'scheduler',
    ref: '§5.1',
    label: 'Starvation alert threshold',
    effect:
      'Marketing with no deadline may starve by design. After this many days queued, a notice states the outstanding count.',
  },
  {
    key: 'scheduler.recipient_domain_daily_cap',
    default: 5,
    section: 'scheduler',
    ref: '§5.2 gate 6',
    label: 'Recipient-domain daily cap (global)',
    effect:
      'Counted ONCE across every sending domain and mailbox. Counting it per sending domain would turn domain rotation into a way to bypass the protection.',
  },
  {
    key: 'scheduler.min_gap_minutes_low',
    default: 3,
    section: 'scheduler',
    ref: '§5.2',
    label: 'Minimum gap between sends — lower bound',
    effect: 'Randomised per mailbox. Prevents the metronomic pattern that fingerprints automation.',
  },
  {
    key: 'scheduler.min_gap_minutes_high',
    default: 10,
    section: 'scheduler',
    ref: '§5.2',
    label: 'Minimum gap between sends — upper bound',
    effect: 'The gap for each pair of consecutive sends is drawn between the two bounds.',
  },
  {
    key: 'scheduler.human_capacity_per_user_per_day',
    default: 12,
    section: 'scheduler',
    ref: '§6.3',
    label: 'Human capacity budget',
    effect:
      'Calls, LinkedIn touches and research tasks draw from this, never from mailbox quota — so a heavy call day cannot distort email pacing.',
  },
  {
    key: 'scheduler.jit_lookahead_hours',
    default: 24,
    section: 'scheduler',
    ref: '§6.1',
    label: 'JIT generation lookahead',
    effect:
      'How far before visible_at a draft is generated. Copy is written against fresh context, and guards run with a day of slack.',
  },

  // ---- Caps & warm-up (§5.3) -------------------------------------------
  {
    key: 'caps.mailbox_warmup_start',
    default: 20,
    section: 'caps',
    ref: '§5.3',
    label: 'Mailbox warm-up start',
    effect: 'Day-one daily cap for a new mailbox. ~27x below Google’s 2,000/day technical limit, deliberately.',
  },
  {
    key: 'caps.mailbox_warmup_increment',
    default: 2,
    section: 'caps',
    ref: '§5.3',
    label: 'Mailbox warm-up increment',
    effect: 'Added per day since first send, until the ceiling.',
  },
  {
    key: 'caps.mailbox_ceiling',
    default: 75,
    section: 'caps',
    ref: '§5.3',
    label: 'Mailbox daily ceiling',
    effect: 'Upper end of mainstream cold-outbound practice (C5 puts guidance at 20–50, some sources 75–100).',
  },
  {
    key: 'caps.domain_warmup_start',
    default: 40,
    section: 'caps',
    ref: '§5.3',
    label: 'Sending-domain warm-up start',
    effect:
      'Independent of mailbox warm-up — reputation does not transfer between domains, so a new domain ramps from cold.',
  },
  {
    key: 'caps.domain_warmup_increment',
    default: 10,
    section: 'caps',
    ref: '§5.3',
    label: 'Sending-domain warm-up increment',
    effect: 'Added per day since the domain’s first send.',
  },
  {
    key: 'caps.idle_days_threshold',
    default: 14,
    section: 'caps',
    ref: '§5.3',
    label: 'Idle-decay threshold',
    effect:
      'After this many days with no sends, the cap re-ramps from max(warmup_start, 50% of earned). Reputation decays with inactivity.',
  },
  {
    key: 'caps.test_sends_per_admin_per_day',
    default: 20,
    section: 'caps',
    ref: '§16.3',
    label: 'Diagnostic test sends per admin per day',
    effect:
      'How many Admin → Mailboxes test sends one administrator may fire in a day. A test send is real mail from a real mailbox and counts against that mailbox’s reputation exactly like a prospect email does, so an unpaced diagnostic loop can burn a sending identity while looking like troubleshooting. Counted per person from the audit log, not per mailbox, because the person holding the button is what needs pacing. Set to 0 to disable test sending entirely.',
  },

  // ---- Throttle (§5.3) --------------------------------------------------
  {
    key: 'throttle.bounce_rate_trigger',
    default: 0.05,
    section: 'throttle',
    ref: '§5.3',
    label: 'Hard-bounce rate trigger',
    effect: 'Trailing-window hard-bounce rate above this throttles the mailbox and, in aggregate, its domain.',
  },
  {
    key: 'throttle.unsubscribe_rate_trigger',
    default: 0.01,
    section: 'throttle',
    ref: '§5.3',
    label: 'Unsubscribe rate trigger',
    effect: 'Marketing sends only. One of the three observables ActOS can actually measure.',
  },
  {
    key: 'throttle.reply_collapse_trigger',
    default: 0.5,
    section: 'throttle',
    ref: '§5.3',
    label: 'Reply-collapse trigger',
    effect:
      'Trips when the trailing reply rate falls below this fraction of the mailbox’s own 30-day baseline. Evaluated only with 50+ sends in window.',
  },
  {
    key: 'throttle.factor',
    default: 0.2,
    section: 'throttle',
    ref: '§5.3',
    label: 'Throttle factor',
    effect: 'Cap multiplier while throttled.',
  },
  {
    key: 'throttle.days',
    default: 7,
    section: 'throttle',
    ref: '§5.3',
    label: 'Throttle duration (days)',
    effect: 'How long a throttle holds before automatic recovery.',
  },
  {
    key: 'throttle.trailing_window_days',
    default: 7,
    section: 'throttle',
    ref: '§5.3',
    label: 'Measurement window (days)',
    effect: 'Window over which bounce and unsubscribe rates are computed.',
  },

  // ---- Compliance (§7.2) ------------------------------------------------
  {
    key: 'compliance.reply_stops_sdr',
    default: true,
    section: 'compliance',
    ref: '§7.2',
    label: 'A real reply stops SDR',
    effect: 'Default on. Marketing always stops on a real reply regardless of this setting.',
  },
  {
    key: 'compliance.art14_enabled',
    default: true,
    section: 'compliance',
    ref: '§17.5, R10, C2',
    label: 'GDPR Art. 14 disclosure',
    effect:
      'When on, the notice below is attached once — to the FIRST email to a contact whose details came from prospecting or a bulk import, when their country is flagged (EU/EEA/UK/CA) or cannot be resolved. Off means those first touches carry no disclosure; the per-send audit still records that one was owed.',
  },
  {
    key: 'compliance.art14_text',
    default:
      'You are receiving this one-time notice because we obtained your business contact details ' +
      '(name, job title, business email, employer) from publicly available professional sources ' +
      'and licensed B2B data providers. We process this data on the basis of legitimate interest ' +
      '(GDPR Art. 6(1)(f)) to send relevant business communication, and retain it only as long as ' +
      'needed for this purpose. You may request access, correction or deletion, or object to ' +
      'further processing, at any time by replying to this email. You may also lodge a complaint ' +
      'with your supervisory authority.',
    section: 'compliance',
    ref: '§17.5, R10, C2',
    label: 'Art. 14 disclosure text',
    effect:
      'Rendered in small print after the postal address, on exactly the messages the toggle above describes. Article 14 requires naming the source of the data and the person’s rights — keep both if you edit.',
    multiline: true,
  },

  // ---- Guards (§6.1) ----------------------------------------------------
  {
    key: 'guards.min_words',
    default: 40,
    section: 'guards',
    ref: '§6.1',
    label: 'Minimum body words',
    effect:
      'Below this the Length guard fails and the task is marked needs_review. Not applied to a one-off drafted from a rep brief (§15) — that email is as short as the brief needs.',
  },
  {
    key: 'guards.max_words',
    default: 180,
    section: 'guards',
    ref: '§6.1',
    label: 'Maximum body words',
    effect: 'Above this the Length guard fails.',
  },
  {
    key: 'guards.judge_model',
    default: 'claude-haiku-4-5-20251001',
    section: 'guards',
    ref: '§6.1',
    label: 'Guard judge model',
    effect:
      'The cheap model that catches semantic leakage a wordlist cannot — "I saw you visited our pricing page" has no banned word. It runs on every draft, so cost matters more than capability here.',
    dynamicChoices: 'ai_models',
  },
  {
    key: 'guards.writer_model',
    default: 'claude-sonnet-5',
    section: 'guards',
    ref: '§6.1',
    label: 'Draft writer model',
    effect:
      'Writes the copy a prospect reads. Worth more capability than the judge: this is the output, not the check on it.',
    dynamicChoices: 'ai_models',
  },

  // ---- Tracking (§6.4) --------------------------------------------------
  {
    key: 'tracking.sdr_pixel_enabled',
    default: true,
    section: 'tracking',
    ref: '§6.4',
    label: 'SDR open tracking',
    effect: 'Marketing is always tracked. SDR is configurable because a pixel is what makes 1:1 mail stop looking hand-written.',
  },
  {
    key: 'marketing.require_content_review',
    default: false,
    section: 'guards',
    ref: 'plans/marketing-redesign.md R7',
    label: 'Content review before use',
    effect:
      'Off by default: a block, template or audience is usable the moment it is saved, and the one approval is the email’s — which pins and locks every version it uses. On, the library review queue returns: content is submitted, an admin approves it there, and only approved content can be used.',
  },
  {
    key: 'tracking.sdr_link_rewriting_enabled',
    default: false,
    section: 'tracking',
    ref: '§6.4',
    label: 'SDR link rewriting',
    effect: 'Off by default. Rewritten links are the strongest "this was automated" tell in a 1:1 email.',
  },
  {
    key: 'tracking.bot_threshold_seconds',
    default: 20,
    section: 'tracking',
    ref: '§6.4',
    label: 'Bot proximity threshold',
    effect:
      'Any open or click within this many seconds of the send timestamp is classified as scanner traffic. Gmail, Apple MPP and SafeLinks fetch on delivery, so something in this window is almost never a human — but a real person who was already reading their inbox can beat a long threshold, and their click would be discarded. Raw counts are always kept, so lowering it is reversible in analysis.',
  },
  {
    key: 'tracking.event_retention_days',
    default: 90,
    section: 'tracking',
    ref: '§6.4',
    label: 'Tracking IP/UA retention (days)',
    effect:
      'How long an open or click keeps the recipient’s IP address and user agent before the retention sweep nulls both. The event itself, and whether it was judged a bot, are kept forever — only the two personal-data columns expire. This is a real trade: those columns exist so the bot filter can be re-tuned against past traffic, and a window shorter than the period you would ever want to re-analyse quietly gives that up. Ninety days keeps a full quarter re-tunable; going below about thirty makes retroactive tuning theatre.',
  },

  // ---- Ranking (§8.3) ---------------------------------------------------
  {
    key: 'ranking.w1_signal_recency_intensity',
    default: 0.35,
    section: 'ranking',
    ref: '§8.3',
    label: 'Weight: signal recency & intensity',
    effect: 'Calibrate in Phase 8 against real outbound rather than guessing now.',
  },
  {
    key: 'ranking.w2_icp_persona_match',
    default: 0.3,
    section: 'ranking',
    ref: '§8.3',
    label: 'Weight: ICP / persona match',
    effect: 'Reads the Library personas. With no personas authored, this input contributes nothing.',
  },
  {
    key: 'ranking.w3_inbox_engagement_history',
    default: 0.15,
    section: 'ranking',
    ref: '§8.3',
    label: 'Weight: inbox engagement history',
    effect: 'Bot-filtered engagement only. Opens are weak-weighted; replies dominate.',
  },
  {
    key: 'ranking.w4_crm_state_and_pipeline',
    default: 0.2,
    section: 'ranking',
    ref: '§8.3',
    label: 'Weight: CRM state & pipeline',
    effect:
      'Also a SAFETY input, not only a ranking one: an account with a live open opportunity is de-prioritised, never cold-emailed.',
  },

  // ---- AI (§9.2) --------------------------------------------------------
  {
    key: 'ai.data_policy',
    default: 'derived_only',
    section: 'ai',
    ref: '§9.2, §7.3.1',
    label: 'AI data policy',
    effect:
      'Governs two things at once: what may be sent to an AI provider, and whether inbound message bodies are stored in this environment at all.',
    choices: [
      {
        value: 'derived_only',
        label: 'Derived only — nothing a prospect wrote leaves or is stored',
        effect:
          'Only structured facts reach a provider: names, titles, industries, signal kinds, approved Library copy. No inbound body is persisted, and the Replies view fetches from Gmail on demand. The strictest setting, and the default.',
      },
      {
        value: 'raw_zero_retention',
        label: 'Raw, zero retention — message content may be sent under enterprise terms',
        effect:
          'Message content may be sent to a provider covered by a zero-retention agreement, and inbound bodies are stored with the TTL below. Only choose this if such an agreement is actually in place — the setting does not create one.',
      },
      {
        value: 'unrestricted',
        label: 'Unrestricted — no limit on what is sent or retained',
        effect:
          'Everything is permitted: raw message content reaches providers under whatever their standard terms are, and inbound bodies are stored with no expiry. Nothing in ActOS will refuse a request on data-governance grounds. Appropriate only where the environment owner has decided that explicitly.',
      },
    ],
  },
  {
    key: 'ai.oneoff_context_scope',
    default: 'thread',
    section: 'ai',
    ref: '§15, §9.2',
    label: 'One-off compose: correspondence scope',
    effect:
      'How much of the prior correspondence the writer of a one-off email is shown. This dial is SCOPE only — whether received CONTENT may travel at all is the AI data policy above: under derived_only the writer learns that they replied and when, never what they wrote.',
    choices: [
      {
        value: 'last_sent',
        label: 'Last sent email',
        effect:
          'The writer sees only the most recent email on record as sent to this person — by ActOS, or captured from the rep’s own mailbox by the compose-time sweep (§15). Nothing they wrote back, and nothing older. The narrowest setting.',
      },
      {
        value: 'thread',
        label: 'All emails (last 10)',
        effect:
          'The writer sees the last 10 messages both ways — ActOS sends, mail the rep sent from their own mailbox (§15), and replies. Received content still requires a permissive AI data policy; under derived_only those entries degrade to reply facts (kind and date only).',
      },
    ],
  },
  {
    key: 'ai.classifier_model',
    default: 'claude-haiku-4-5-20251001',
    section: 'ai',
    ref: '§7.3',
    label: 'Inbound classifier model',
    effect:
      'The fourth and last classification tier, used only for inbound that headers, bounce reports and heuristics could not settle. It reads what a prospect wrote, so it is skipped entirely under derived_only.',
    dynamicChoices: 'ai_models',
  },
  {
    key: 'ai.inbound_body_ttl_days',
    default: 30,
    section: 'ai',
    ref: '§7.3.1',
    label: 'Inbound body retention (days)',
    effect:
      'Applies to raw_zero_retention only. Ignored under derived_only, where no body is stored, and under unrestricted, where nothing expires.',
  },

  // ---- Prospecting (§17.5) ---------------------------------------------
  {
    key: 'prospecting.vendor',
    default: 'amplemarket',
    section: 'prospecting',
    ref: '§17.5, §4.2, §8.2',
    label: 'People-data vendor',
    effect:
      'Which connected vendor every people search, reveal, email check, company enrichment, company discovery and job-change check goes to — and whose cost sentences the pages show. Only a vendor with an API key under Admin → Connectors can be chosen; the other options are disabled with the reason. A prospecting run keeps the vendor it was searched on for its reveals and injections even after this changes; job-change checks and adopts use whatever is selected at the time.',
    choices: [
      {
        value: 'amplemarket',
        label: 'Amplemarket',
        effect:
          'Cost model: searching and market-wide company discovery are free, so a preview page can be generous. Revealing a person costs 1 email credit when an address is found and 0.5 when the lookup finds no address. Address validation is billed by Amplemarket per address checked. A job-change check costs 0.5 email credits per person found. Company enrichment by domain costs no email credit. Up to 100 people per page and 10 values per filter.',
      },
      {
        value: 'contactout',
        label: 'ContactOut',
        effect:
          'Cost model: every person a search SHOWS costs 1 search credit (pages are capped at 25), so the preview itself is a spend — counting matches first is free, and the Search button states the maximum before it runs. Revealing a person costs 1 email credit only when a work address is found, and nothing when it is not. Address verification draws on a separate verifier-credit pool, billed only for valid, invalid or accept-all answers; addresses ContactOut already marked Verified at reveal skip it. A job-change check costs 1 search credit per person found. Company enrichment by domain and company discovery cost 1 search credit per company. Up to 50 values per filter.',
      },
    ],
  },
  {
    key: 'prospecting.limit_per_persona',
    default: 3,
    section: 'prospecting',
    ref: '§17.5',
    label: 'Reveal limit per ICP persona',
    effect:
      'Caps how many people one run may reveal for each persona. Doubles as the spend control — the selected vendor bills per contact revealed (Amplemarket per reveal attempted, ContactOut only when an address is found).',
  },
  {
    key: 'prospecting.max_contacts_per_account',
    default: 4,
    section: 'prospecting',
    ref: '§17.5, §5.2 gate 6',
    label: 'Maximum contacts per account',
    effect:
      'Counted across everyone the account already has, not per run. Injecting eight people at one company queues exactly the pattern gate 6’s global recipient-domain cap exists to suppress, and a second run must not be a way around the first.',
  },
  {
    key: 'prospecting.preview_page_size',
    default: 25,
    section: 'prospecting',
    ref: '§17.5',
    label: 'People shown per persona in the review preview',
    effect:
      'How many people each persona’s search shows for review. On Amplemarket a search costs nothing, so the page can be generous and only a reveal is billed. On ContactOut every person shown costs one search credit and a page is capped at 25, so this is a spend control there — the Search button states the maximum before it runs. A larger page means a human chooses from more people, not that more are contacted.',
  },
  {
    key: 'prospecting.marketing_max_per_run',
    default: 100,
    section: 'prospecting',
    ref: '§17.5',
    label: 'Most people one Find-new-people run may add',
    effect:
      'A marketer’s run searches for people by title, industry, size and country and reveals the ones they tick, in the background, a few at a time. This caps one run, and with it the email credits one click can spend: every person revealed bills the selected vendor’s reveal rate. The per-company cap above still applies within a run. It bounds one run, not the month.',
  },

  // ---- Job-change tracking (§8.2) ---------------------------------------
  //
  // There is no job-change API on either vendor. The signal is derived by
  // re-enriching a person and diffing the answer against a stored baseline —
  // which bills the selected vendor's job-check rate per person found
  // (Amplemarket 0.5 email credits; ContactOut 1 search credit). These three
  // dials ARE the spend control, the way the reveal limit is the spend
  // control for prospecting.
  {
    key: 'jobchange.enabled',
    default: true,
    section: 'jobchange',
    ref: '§8.2',
    label: 'Watch tracked contacts for job changes',
    effect:
      'Off means the stage does nothing and costs nothing. On, it re-enriches contacts in a live cadence and raises a research task when one has moved.',
  },
  {
    key: 'jobchange.recheck_days',
    default: 30,
    section: 'jobchange',
    ref: '§8.2',
    label: 'Days between checks on one contact',
    effect:
      'Each check bills the selected vendor’s job-check rate per person found — 0.5 email credits on Amplemarket, 1 search credit on ContactOut. Monthly is the deliberate floor: profiles are updated slowly, and checking weekly would quadruple the cost to learn the same thing later.',
  },
  {
    key: 'jobchange.batch_size',
    default: 50,
    section: 'jobchange',
    ref: '§8.2',
    label: 'Contacts checked per tick',
    effect:
      'Bounds one tick, not the month. The ceiling on total spend is the size of the tracked set — contacts in a live cadence — not this number.',
  },
  {
    key: 'jobchange.max_misses',
    default: 3,
    section: 'jobchange',
    ref: '§8.2',
    label: 'Consecutive misses before a contact stops being checked',
    effect:
      'A vendor not knowing someone is a permanent answer, not a transient one. Without this we would pay the per-person check rate every month forever for a person their database has never held.',
  },

  // ---- Address confidence (§17.5 readiness gate) ------------------------
  //
  // The gate used to be a boolean — "deliverable and not catch-all". That made
  // one bit stand in for two independent things: how much the SOURCE is worth,
  // and what the CHECK found. These dials separate them, so a catch-all domain
  // lowers certainty rather than failing everyone on it.
  {
    key: 'verification.min_confidence',
    default: 0.7,
    section: 'verification',
    ref: '§17.5',
    label: 'Minimum address confidence to enroll',
    effect:
      'Below this, a contact is held and a research task names them for a human to confirm. At the default 0.7, a vendor-revealed address (Amplemarket or ContactOut) on a catch-all domain (0.8) is accepted while a weaker source on the same domain (0.6) is not.',
  },
  {
    key: 'verification.base_confidence_crm',
    default: 1,
    section: 'verification',
    ref: '§8.1',
    label: 'Base confidence — CRM',
    effect:
      'The CRM is the system of record: if it holds an address, that is the company’s own answer to how to reach the person.',
  },
  {
    key: 'verification.base_confidence_amplemarket',
    default: 1,
    section: 'verification',
    ref: '§17.5',
    label: 'Base confidence — Amplemarket',
    effect:
      'Addresses revealed by Amplemarket are high quality in practice, so the base is full and the catch-all penalty carries the one real doubt.',
  },
  {
    key: 'verification.base_confidence_contactout',
    default: 1,
    section: 'verification',
    ref: '§17.5',
    label: 'Base confidence — ContactOut',
    effect:
      'Addresses revealed by ContactOut come through its real-time work-email finder, which verifies as it finds, so the base is full and the catch-all penalty carries the one real doubt. Lower it if the trial shows a weaker hit rate on your ICP than Amplemarket’s.',
  },
  {
    key: 'verification.base_confidence_manual',
    default: 0.9,
    section: 'verification',
    ref: '§8.1',
    label: 'Base confidence — typed in by hand',
    effect: 'A person asserted it. Usually right, occasionally a typo, and nothing checked it.',
  },
  {
    key: 'verification.base_confidence_import',
    default: 0.8,
    section: 'verification',
    ref: '§8.1',
    label: 'Base confidence — bulk import',
    effect:
      'A file from somewhere, with provenance nobody can reconstruct. Lower the base here and a catch-all domain pushes imports under the threshold, which is usually what you want.',
  },
  {
    key: 'verification.catch_all_penalty',
    default: 0.2,
    section: 'verification',
    ref: '§17.5',
    label: 'Penalty — catch-all domain',
    effect:
      'A catch-all domain accepts mail for EVERY address, so a “deliverable” verdict on one is the domain answering rather than the mailbox. Reason to be less certain, not reason to refuse.',
  },
  {
    key: 'verification.risky_penalty',
    default: 0.4,
    section: 'verification',
    ref: '§17.5',
    label: 'Penalty — flagged risky',
    effect:
      'Full mailbox, disposable address, or otherwise likely to bounce. Bounces are what quarantine a warming domain (§5.3), so this is the heaviest penalty.',
  },
  {
    key: 'verification.unknown_penalty',
    default: 0.25,
    section: 'verification',
    ref: '§17.5',
    label: 'Penalty — checker got no answer',
    effect: 'Absence of evidence rather than evidence of absence, so it costs less than a risky verdict.',
  },
  {
    key: 'verification.never_checked_penalty',
    default: 0,
    section: 'verification',
    ref: '§17.5',
    label: 'Penalty — never checked',
    effect:
      'Zero by default: not having looked is not a reason to doubt an address, because the base already says how much the source is worth. Raise it to make unchecked addresses need confirming.',
  },

  // ---- Bulk jobs — the durable substrate for work too big for one request --
  //
  // The single dial that matters is the subrequest budget: it is the ONE
  // number that has to change when the Cloudflare plan changes, and having it
  // here rather than hardcoded is what makes "robust regardless of tier"
  // literally true instead of aspirational.
  {
    key: 'jobs.subrequest_budget',
    default: 45,
    section: 'jobs',
    ref: '§A',
    label: 'Subrequest budget per invocation',
    effect:
      'How much work one slice may do. Cloudflare allows 50 subrequests per invocation on Workers Free and 1000 on Paid, and every SQL statement counts as one — so this is set just under the Free ceiling. RAISE IT TO ~950 AFTER UPGRADING TO WORKERS PAID: bulk imports and enrollments then finish in one slice instead of many, with no other change.',
  },
  {
    key: 'jobs.drain_enabled',
    default: true,
    section: 'jobs',
    ref: '§A',
    label: 'Run the bulk-job drain',
    effect:
      'The second cron trigger that advances queued bulk work on every drain tick. Off means jobs stay where they are — safe, resumable, and visibly not moving; nothing is lost.',
  },
  {
    key: 'jobs.max_items_per_slice',
    default: 50,
    section: 'jobs',
    ref: '§A',
    label: 'Maximum items per slice',
    effect:
      'A ceiling on top of the budget arithmetic, so a cheap job cannot claim thousands of items into one invocation and hit the CPU limit instead of the subrequest one.',
  },
  {
    key: 'jobs.lease_seconds',
    default: 240,
    section: 'jobs',
    ref: '§A',
    label: 'Slice lease (seconds)',
    effect:
      'How long a slice owns a job and its items before another may reclaim them. Deliberately shorter than the 5-minute drain interval, so a worker that dies mid-slice frees its work before the next drain arrives.',
  },
  {
    key: 'jobs.max_item_attempts',
    default: 3,
    section: 'jobs',
    ref: '§A',
    label: 'Maximum attempts per item',
    effect:
      'Evaluated INSIDE the claim, so an item that kills its worker every time is recorded failed on the third attempt rather than being reclaimed forever. Without this the substrate would reproduce the livelock it exists to remove.',
  },
  {
    key: 'jobs.max_zero_progress_slices',
    default: 5,
    section: 'jobs',
    ref: '§A',
    label: 'Slices without progress before stalling',
    effect:
      'After this many consecutive slices that complete nothing, the job is marked stalled and raises a notice. A stalled job is never auto-resumed — it waits for a human, which is the same discipline as the dead-letter queue.',
  },
  {
    key: 'jobs.inline_unit_threshold',
    default: 25,
    section: 'jobs',
    ref: '§A',
    label: 'Run inline below this many items',
    effect:
      'Work smaller than this runs in the request itself and returns its results immediately — no job row, no waiting. Above it, the request returns a job id and the drain finishes it. Keep it comfortably inside one slice.',
  },
  {
    key: 'jobs.retention_days',
    default: 14,
    section: 'jobs',
    ref: '§A',
    label: 'Keep finished jobs for (days)',
    effect:
      'Completed and cancelled jobs are deleted after this, with their per-item results. The audit trail in outbound_events is permanent and unaffected.',
  },

  // ---- Reporting (§17.4) ------------------------------------------------
  {
    key: 'reporting.min_arm_sample',
    default: 100,
    section: 'ranking',
    ref: '§17.4',
    label: 'Minimum sample per block variant',
    effect:
      'Below this the per-block comparison shows "not enough data" instead of a winner. At 412 recipients across 4 variants with a 2% click rate, each arm produces ~2 clicks.',
  },
  {
    key: 'reporting.significance_p',
    default: 0.05,
    section: 'ranking',
    ref: '§17.4',
    label: 'Significance threshold',
    effect: 'Two-proportion test must clear this before a block variant is declared better.',
  },

  // ---- Booking (plans/booking-mechanism.md) -----------------------------
  //
  // Four numbers that were constants in `routes/booking-public.ts`. Each one
  // decides something a client can reasonably disagree with — how long a link
  // survives in an old email, how far ahead a stranger may reach into a rep's
  // calendar — and changing a constant means a deploy.
  {
    key: 'booking.link_ttl_days',
    default: 180,
    section: 'booking',
    ref: '§6',
    label: 'Booking link lifetime (days)',
    effect:
      'How long a ?k= attribution token stays valid. Past it the link still opens, but as the rep’s plain link with no attribution — the deliberate asymmetry that keeps a forwarded link working. The booking_links row is the real revocation point: delete it and the token is inert whatever this says.',
  },
  {
    key: 'booking.max_query_days',
    default: 60,
    section: 'booking',
    ref: '§6',
    label: 'Maximum availability window (days)',
    effect:
      'Caps how far ahead one free/busy call reaches, whatever a profile’s horizon says. Google limits the span it will answer for, and this keeps a generous profile from asking for more than it will give.',
  },
  {
    key: 'booking.max_open_per_invitee',
    default: 3,
    section: 'booking',
    ref: '§6',
    label: 'Future meetings one invitee may hold',
    effect:
      'A stranger with the link can otherwise fill a rep’s week. Counted per (host, invitee email) over confirmed future meetings; the refusal tells them to reply to an existing invitation instead.',
  },
  {
    key: 'booking.ics_ttl_days',
    default: 30,
    section: 'booking',
    ref: '§8',
    label: 'Backup invitation lifetime (days)',
    effect:
      'How long the .ics download link served after a booking keeps working. The file carries both parties’ names, addresses and the meeting subject, so a link pasted into a shared document should stop serving it eventually.',
  },

  // ---- External send API (plans/external-send-api.md §7) -----------------
  //
  // Every one of these defaults to the closed position. The surface ships
  // dark: an environment turns it on deliberately, and turns auto-send on
  // separately, and only when an integration actually needs it.
  {
    key: 'api.enabled',
    default: false,
    section: 'api',
    ref: 'E7 §7',
    label: 'External send API',
    effect:
      'The external send API answers requests. Off: every /v1 request is refused with api_disabled, tokens included — the kill switch. Tokens are kept, and work again the moment this is back on.',
  },
  {
    key: 'api.allow_auto_send',
    default: false,
    section: 'api',
    ref: 'E7 §1.2',
    label: 'Allow sending without review',
    effect:
      'Tokens may carry “Send without review” and send email nobody approved. Re-checked on every request, so turning this off disarms every such token immediately. This is the one place ActOS sends unreviewed email: the content guards stand in for the reviewer, and a draft that fails one is never sent this way.',
  },
  {
    key: 'api.default_daily_limit',
    default: 25,
    section: 'api',
    ref: 'E7 A15',
    label: 'Emails per token per day',
    effect:
      'Emails one token may create per UTC day — each enrollment into a cadence counts as one too — and the ceiling a rep may set on their own token. Counted, never estimated, and never fails open — it is what stops one misbehaving integration from spending a mailbox’s whole cap before nine.',
  },
  {
    key: 'api.max_batch',
    default: 25,
    section: 'api',
    ref: 'E7 §4.6',
    label: 'Emails per batch request',
    effect:
      'Items one batch request may carry. Batches are validated whole and then drained as a bulk job, so a larger batch is not faster — it is a longer tail.',
  },
  {
    key: 'api.html_max_bytes',
    default: 102400,
    section: 'api',
    ref: 'E7 A9',
    label: 'Largest supplied HTML (bytes)',
    effect:
      'Largest HTML body an integration may supply. Gmail clips a message at about 102 KB and hides the rest behind “[Message clipped]”, which is why the default sits there.',
  },
  {
    key: 'api.allow_any_time_window',
    default: false,
    section: 'api',
    ref: 'E7 A19',
    label: 'Allow sending outside business hours',
    effect:
      'Tokens may carry “Send outside business hours”, and an email sent without review may then ask to pass the business-hours gate for that one send. A queued email never can — a person approves it into business hours, because it leaves from their mailbox. Every other gate still applies.',
  },
  {
    key: 'api.callback_hosts',
    default: '',
    section: 'api',
    ref: 'E7 §4.7',
    label: 'Callback hosts',
    multiline: true,
    effect:
      'Hostnames a token’s callback URL may point at, one per line, https only. Empty means callbacks are refused: ActOS fetches nothing on a caller’s behalf that is not on this list.',
  },
] as const satisfies readonly PolicyDial[];

export type PolicyKey = (typeof POLICY_DIALS)[number]['key'];

export const POLICY_DEFAULTS: Record<string, unknown> = Object.fromEntries(
  POLICY_DIALS.map((d) => [d.key, d.default]),
);

export function dialsForSection(section: PolicySection): readonly PolicyDial[] {
  return POLICY_DIALS.filter((d) => d.section === section);
}

/**
 * §9.2 — the policy modes, named once.
 *
 * `unrestricted` is a deliberate escape hatch, not a default. It exists because
 * some environments genuinely have no constraint and forcing them through
 * `raw_zero_retention` would misrepresent an agreement they do not have.
 */
export type AiDataPolicy = 'derived_only' | 'raw_zero_retention' | 'unrestricted';

/** Whether this mode permits raw prospect content to reach a provider. */
export function allowsRawContent(policy: AiDataPolicy): boolean {
  return policy === 'raw_zero_retention' || policy === 'unrestricted';
}

/**
 * §15 — how much prior correspondence a one-off draft's writer is shown.
 *
 * Deliberately a separate axis from `AiDataPolicy`: this decides SCOPE (one
 * email or the last ten), the data policy decides whether received CONTENT may
 * travel at all. The two compose — 'thread' under `derived_only` means ten
 * entries of which the received ones are facts, not words.
 */
export type OneOffContextScope = 'last_sent' | 'thread';

/** Whether inbound bodies are persisted at all under this mode (§7.3.1). */
export function storesInboundBodies(policy: AiDataPolicy): boolean {
  return allowsRawContent(policy);
}

/** null means "never expires" — only `unrestricted` returns that. */
export function inboundBodyTtlDays(policy: AiDataPolicy, configured: number): number | null {
  if (policy === 'derived_only') return 0;
  if (policy === 'unrestricted') return null;
  return configured;
}
